import _ from "lodash";
/**--internal -- */
import {
  CURRICULUM_TYPE_PYP,
  ELEMENT_TYPE_MYP_ATL,
  ELEMENT_TYPE_MYP_KEY_CONCEPT,
  ELEMENT_TYPE_MYP_LEARNER_PROFILE,
  ELEMENT_TYPE_MYP_OBJECTIVES,
  ELEMENT_TYPE_UBD_ATL,
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_UBD,
  ELEMENT_TYPE_MYP_LEARNING_STANDARD,
  ELEMENT_TYPE_UBD_LEARNING_STANDARD,
  ELEMENT_TYPE_MYP_SUBJECT,
  ELEMENT_TYPE_UBD_SUBJECT,
  ELEMENT_TYPE_MYP_SERVICE_ACTION_OUTCOME,
  ELEMENT_TYPE_DP_CAS_LEARNING_OUTCOMES,
  ELEMENT_TYPE_DP_CP_SL_LEARNING_OUTCOMES,
} from "Constants/stringConstants";
import { getFormattedUserName } from "Utils/GetFormattedUserName";
import { getYearDurationFormat } from "Utils";
import ACLStore from "lib/aclStore";
import { getIsLearningCoursesEnabled } from "Teacher/routes/LearningCourses/modules/Utils";
import { LEARNING_COURSE_FILTER_IN_SNP } from "Constants/permissionConstants";

/**--relative-- */
import {
  filterLabelConfig,
  tabFiltersConfigWithLearningCourse,
  tabFiltersConfig,
} from "./Config";

const CURRICULUM_TYPE_TO_SUBJECT_TYPE_MAPPING = {
  [CURRICULUM_TYPE_PYP]: "SUBJECT",
  [CURRICULUM_TYPE_MYP]: ELEMENT_TYPE_MYP_SUBJECT,
  [CURRICULUM_TYPE_UBD]: ELEMENT_TYPE_UBD_SUBJECT,
};

const CURRICULUM_TYPE_TO_LEARNING_STANDARD_MAPPING = {
  [CURRICULUM_TYPE_MYP]: ELEMENT_TYPE_MYP_LEARNING_STANDARD,
  [CURRICULUM_TYPE_UBD]: ELEMENT_TYPE_UBD_LEARNING_STANDARD,
};

const getUpdatedAvailableFilters = ({
  availableFilters,
  projectNodeUID,
  isProjectNode,
}) => {
  if (isProjectNode) {
    switch (projectNodeUID) {
      case "CP_DETAILS":
      case "PP_DETAILS":
        return [ELEMENT_TYPE_MYP_ATL, ELEMENT_TYPE_MYP_OBJECTIVES];
      case "SL_PROJECT":
        return [ELEMENT_TYPE_DP_CP_SL_LEARNING_OUTCOMES];
      case "CAS_PROJECT":
        return [ELEMENT_TYPE_DP_CAS_LEARNING_OUTCOMES];
      case "SA_ACTIVITY":
        return [ELEMENT_TYPE_MYP_SERVICE_ACTION_OUTCOME];
      default:
        return [];
    }
  } else {
    return availableFilters;
  }
};

const getUpdatedModuleFacets = ({
  data,
  curriculumType,
  activeTab,
  isProjectNode,
  projectNodeUID,
  currentTabFiltersConfig,
}) => {
  const moduleFacets = _.get(data, "globalConstants.moduleFacets", []);

  const updatedModuleFacets = _.map(moduleFacets, ({ entityType, ...rest }) => {
    const attributes = _.get(filterLabelConfig, `${entityType}.attributes`, []);

    if (_.isEmpty(attributes)) {
      return { ...rest, entityType };
    }
    return { ...rest, entityType, attributes };
  });

  const availableFilters = _.get(
    currentTabFiltersConfig,
    `${activeTab}.curriculumWiseAvailableFilters.${curriculumType}`,
    []
  );

  let updatedAvailableFilters = getUpdatedAvailableFilters({
    projectNodeUID,
    isProjectNode,
    availableFilters,
  });

  if (!ACLStore.can("FeatureFlag:ShowFolderFilterInEvidenceModal")) {
    updatedAvailableFilters = _.filter(
      updatedAvailableFilters,
      filter => filter !== "EVIDENCE_FOLDER"
    );
  }

  return _.filter(updatedModuleFacets, ({ entityType }) => {
    return _.includes(updatedAvailableFilters, entityType);
  });
};

export const getUpdatedModuleFacetsMemoized = _.memoize(
  getUpdatedModuleFacets,
  ({
    activeTab,
    curriculumType,
    isProjectNode,
    projectNodeUID,
    data,
    currentTabFiltersConfig,
  }) => {
    return JSON.stringify({
      activeTab,
      curriculumType,
      isProjectNode,
      projectNodeUID,
      data: {
        globalConstants: {
          moduleFacets: _.map(
            data?.globalConstants?.moduleFacets,
            moduleFacet =>
              _.pick(moduleFacet, ["entityType", "levels", "attributes"])
          ),
        },
      },
      currentTabFiltersConfig: {
        isLearningCourseConfig: currentTabFiltersConfig.isLearningCourseConfig,
      },
    });
  }
);

const getSelectedObjectiveIds = ({ selectedObjectives }) => {
  const objectiveIds = [];

  _.forEach(selectedObjectives, ({ key, values = [] }) => {
    if (key == "INTERDISCIPLINARY_CRITERIA") {
      objectiveIds.push(...values);
    } else {
      /**
       * For objectives of subject, we need to go one
       * level deep(subject->yearly objectives)
       */
      const subGroups = values;
      _.forEach(subGroups, ({ values }) => {
        objectiveIds.push(...values);
      });
    }
  });

  return objectiveIds;
};

const getFilterPropFromState = ({
  activeTab,
  curriculumType,
  appliedFilters,
  isProjectNode,
}) => {
  const filterProp = {};

  switch (activeTab) {
    case "UNIT_EVIDENCE":
    case "REFLECTION_POST":
    case "PROGRESS_REPORT_POST": {
      const learningCourseOptions = appliedFilters?.LEARNING_COURSE || [];

      if (!_.isEmpty(learningCourseOptions)) {
        filterProp.learningCourseIds = learningCourseOptions;
      }

      const courseOptions = appliedFilters.COURSE || {};

      const selectedCourses = _.flatMap(courseOptions, ({ values }) => values);
      if (!_.isEmpty(selectedCourses)) {
        filterProp.courseIds = _.uniq(selectedCourses);
      }

      const selectedUnits = appliedFilters.UNIT_PLAN || [];

      if (!_.isEmpty(selectedUnits)) {
        filterProp.unitPlanIds = selectedUnits;
      }

      const selectedAcademicYears = appliedFilters.ACADEMIC_YEAR || [];

      if (!_.isEmpty(selectedAcademicYears)) {
        filterProp.academicYears = selectedAcademicYears;
      }

      const selectedEvidenceFolderOptions =
        appliedFilters.EVIDENCE_FOLDER || [];
      if (!_.isEmpty(selectedEvidenceFolderOptions)) {
        const filterFolderIds = _.get(filterProp, "folderIds", []);
        filterProp.folderIds = _.uniq([
          ...filterFolderIds,
          ...selectedEvidenceFolderOptions,
        ]);
      }

      const plannerElements = [
        { entityType: "ATL", plannerElementType: "ATL" },
        { entityType: "CONCEPT", plannerElementType: "CONCEPT" },
        {
          entityType: ELEMENT_TYPE_MYP_KEY_CONCEPT,
          plannerElementType: ELEMENT_TYPE_MYP_KEY_CONCEPT,
        },
        {
          entityType: "LEARNER_PROFILE",
          plannerElementType: "LEARNER_PROFILE",
        },
        {
          entityType: ELEMENT_TYPE_MYP_LEARNER_PROFILE,
          plannerElementType: ELEMENT_TYPE_MYP_LEARNER_PROFILE,
        },
        {
          entityType: ELEMENT_TYPE_MYP_ATL,
          plannerElementType: ELEMENT_TYPE_MYP_ATL,
        },
        {
          entityType: ELEMENT_TYPE_UBD_ATL,
          plannerElementType: ELEMENT_TYPE_UBD_ATL,
        },
        {
          entityType: "SUBJECT",
          plannerElementType:
            CURRICULUM_TYPE_TO_SUBJECT_TYPE_MAPPING[curriculumType],
        },
        ...(isProjectNode
          ? [
              {
                entityType: ELEMENT_TYPE_MYP_OBJECTIVES,
                plannerElementType: ELEMENT_TYPE_MYP_OBJECTIVES,
              },
            ]
          : []),
        ...(isProjectNode
          ? [
              {
                entityType: ELEMENT_TYPE_MYP_SERVICE_ACTION_OUTCOME,
                plannerElementType: ELEMENT_TYPE_MYP_SERVICE_ACTION_OUTCOME,
              },
            ]
          : []),
        ...(isProjectNode
          ? [
              {
                entityType: ELEMENT_TYPE_DP_CAS_LEARNING_OUTCOMES,
                plannerElementType: ELEMENT_TYPE_DP_CAS_LEARNING_OUTCOMES,
              },
            ]
          : []),
        ...(isProjectNode
          ? [
              {
                entityType: ELEMENT_TYPE_DP_CP_SL_LEARNING_OUTCOMES,
                plannerElementType: ELEMENT_TYPE_DP_CP_SL_LEARNING_OUTCOMES,
              },
            ]
          : []),
      ];

      let pypElements = _.reduce(
        plannerElements,
        (result, { entityType, plannerElementType }) => {
          const selectedItems = _.map(appliedFilters[entityType] || [], id => ({
            type: plannerElementType,
            id,
          }));

          return [...result, ...selectedItems];
        },
        []
      );

      let selectedMypObjectives = getSelectedObjectiveIds({
        selectedObjectives: appliedFilters[ELEMENT_TYPE_MYP_OBJECTIVES],
      });

      selectedMypObjectives = _.map(selectedMypObjectives, id => ({
        id,
        type: ELEMENT_TYPE_MYP_OBJECTIVES,
      }));

      pypElements = [...pypElements, ...selectedMypObjectives];

      if (!_.isEmpty(pypElements)) {
        filterProp.pypElements = pypElements;
      }

      const studentIds = appliedFilters.STUDENT || [];
      if (!_.isEmpty(studentIds)) {
        filterProp.studentIds = studentIds;
      }

      return filterProp;
    }
    case "UNIT_PLANS": {
      const selectedGrades = appliedFilters.GRADE || [];
      if (!_.isEmpty(selectedGrades)) {
        filterProp.grades = selectedGrades;
      }

      const selectedAcademicYears = appliedFilters.ACADEMIC_YEAR || [];
      if (!_.isEmpty(selectedAcademicYears)) {
        filterProp.academicYears = selectedAcademicYears;
      }

      const selectedLearningCourses = appliedFilters.LEARNING_COURSE || [];

      if (!_.isEmpty(selectedLearningCourses)) {
        filterProp.learningCourseIds = selectedLearningCourses;
      }

      const unitPlanTypes = appliedFilters.UNIT_PLAN_TYPE || [];
      if (_.size(unitPlanTypes) === 1) {
        filterProp.type = unitPlanTypes[0];
      }

      const selectedThemes = appliedFilters.UNIT_PLAN_THEME || [];
      if (!_.isEmpty(selectedThemes)) {
        filterProp.themes = selectedThemes;
      }

      const selectedSubjects = appliedFilters.SUBJECT || [];
      if (!_.isEmpty(selectedSubjects)) {
        filterProp.subjects = selectedSubjects;
      }

      return filterProp;
    }
    case "LEARNING_EXPERIENCES": {
      const selectedGrades = appliedFilters.GRADE || [];
      if (!_.isEmpty(selectedGrades)) {
        filterProp.grades = selectedGrades;
      }

      const selectedAcademicYears = appliedFilters.ACADEMIC_YEAR || [];

      if (!_.isEmpty(selectedAcademicYears)) {
        filterProp.academicYears = selectedAcademicYears;
      }

      const selectedLearningCourses = appliedFilters.LEARNING_COURSE || [];

      if (!_.isEmpty(selectedLearningCourses)) {
        filterProp.learningCourseIds = selectedLearningCourses;
      }

      const selectedUnits = appliedFilters.UNIT_PLAN || [];
      if (!_.isEmpty(selectedUnits)) {
        filterProp.units = selectedUnits;
      }

      return filterProp;
    }

    case "PROGRESS_REPORTS": {
      const selectedGrades = appliedFilters.GRADE || [];
      if (!_.isEmpty(selectedGrades)) {
        filterProp.gradeIds = selectedGrades;
      }

      const selectedAcademicYears = appliedFilters.ACADEMIC_YEAR || [];
      if (!_.isEmpty(selectedAcademicYears)) {
        filterProp.academicYearIds = selectedAcademicYears;
      }

      const selectedProgressReportTaskGroups =
        appliedFilters.PROGRESS_REPORT_TASK_GROUP || [];
      if (!_.isEmpty(selectedProgressReportTaskGroups)) {
        filterProp.progressReportTaskGroupIds = selectedProgressReportTaskGroups;
      }
      return filterProp;
    }

    case "CLASSROOM": {
      const courseOptions = appliedFilters.COURSE || {};

      const selectedCourses = _.flatMap(courseOptions, ({ values }) => values);
      if (!_.isEmpty(selectedCourses)) {
        filterProp.courseIds = selectedCourses;
      }

      const selectedLearningCourses = appliedFilters.LEARNING_COURSE || [];

      if (!_.isEmpty(selectedLearningCourses)) {
        filterProp.learningCourseIds = selectedLearningCourses;
      }

      const selectedFolderOptions = appliedFilters.FOLDER || [];
      if (!_.isEmpty(selectedFolderOptions)) {
        filterProp.folderIds = selectedFolderOptions;
      }

      const selectedAcademicYears = appliedFilters.ACADEMIC_YEAR || [];
      if (!_.isEmpty(selectedAcademicYears)) {
        filterProp.academicYearIds = selectedAcademicYears;
      }

      const selectedActivityTypes = appliedFilters.ACTIVITY_TYPE || [];
      if (!_.isEmpty(selectedActivityTypes)) {
        filterProp.assessmentTypes = selectedActivityTypes;
      } else {
        filterProp.contentTypes = ["ASSESSMENT", "ASSIGNMENT_RESOURCE"];
      }

      return filterProp;
    }

    case "MYP_PLANNING_INSIGHTS": {
      const selectedAcademicYears = appliedFilters.ACADEMIC_YEAR || [];
      if (!_.isEmpty(selectedAcademicYears)) {
        filterProp.academicYearId = selectedAcademicYears[0];
      }

      const selectedSubjectGroups = appliedFilters.SUBJECT_GROUP || [];
      if (!_.isEmpty(selectedSubjectGroups)) {
        filterProp.subjectGroupId = selectedSubjectGroups[0];
      }

      return filterProp;
    }

    case "POI_INSIGHTS": {
      const selectedAcademicYears = appliedFilters.ACADEMIC_YEAR || [];
      if (!_.isEmpty(selectedAcademicYears)) {
        filterProp.academicYearId = selectedAcademicYears[0];
      }

      return filterProp;
    }
    default: {
      return {};
    }
  }
};

export const getFilterPropFromStateMemoized = _.memoize(
  getFilterPropFromState,
  ({ activeTab, curriculumType, appliedFilters, isProjectNode }) => {
    return JSON.stringify({
      activeTab,
      curriculumType,
      appliedFilters,
      isProjectNode,
    });
  }
);

export const getFilterOptionParam = ({
  entityType,
  curriculumProgramId,
  parentFiltersData,
  curriculumType,
  activeTabClassesFilterOptions,
  excludeBlueprintCourses = false,
  filterByLearningCourse = false,
}) => {
  switch (entityType) {
    case "ATL": {
      return {
        filters: { depth: 1, curriculumProgramIds: [curriculumProgramId] },
      };
    }

    case "SUBJECT": {
      const { values: courses = [] } = parentFiltersData?.COURSE ?? {};
      const { values: learningCourseId = [] } =
        parentFiltersData?.LEARNING_COURSE ?? {};

      const filters = {
        curriculumProgramIds: [curriculumProgramId],
        archivalState: "ALL",
      };

      if (curriculumType == CURRICULUM_TYPE_MYP) {
        filters.courseIds = courses;
      }

      if (filterByLearningCourse) {
        filters.learningCourseId = learningCourseId;
      }

      return { filters };
    }

    case "GRADE": {
      return {
        filters: {
          curriculumProgramIds: [curriculumProgramId],
          learningCourseIds: parentFiltersData?.LEARNING_COURSE?.values,
        },
      };
    }

    case "ACTIVITY_TYPE":
    case "UNIT_PLAN_TYPE":
    case "LEARNER_PROFILE":
    case "CONCEPT": {
      return {
        filters: {
          curriculumProgramIds: [curriculumProgramId],
        },
      };
    }
    case "UNIT_PLAN_THEME": {
      const options = {
        filters: { curriculumProgramIds: [curriculumProgramId] },
      };
      const { values: academicYears = [] } =
        parentFiltersData.ACADEMIC_YEAR ?? {};
      options.filters.academicYear = academicYears[0];
      return options;
    }

    case "LEARNING_COURSE": {
      const options = {
        filters: { curriculumProgramIds: [curriculumProgramId] },
      };

      const { values: academicYears = [] } =
        parentFiltersData.ACADEMIC_YEAR ?? {};
      options.filters.academicYears = [academicYears[0]];
      options.filters.archivalState = "ACTIVE";
      options.filters.fetchAllCourses = true;
      options.filters.types = excludeBlueprintCourses ? ["TEACHER_COURSE"] : [];

      return options;
    }

    case "COURSE": {
      const options = {
        filters: { curriculumProgramIds: [curriculumProgramId] },
      };

      const { values: academicYears = [] } =
        parentFiltersData.ACADEMIC_YEAR ?? {};
      options.filters.academicYear = academicYears[0];

      options.filters.learningCourseIds =
        parentFiltersData?.LEARNING_COURSE?.values;
      return options;
    }

    case "UNIT_PLAN": {
      const options = {
        filters: { curriculumProgramIds: [curriculumProgramId] },
      };

      const { values: grades = [] } = parentFiltersData.GRADE ?? {};
      options.filters.grades = grades;

      const { values: learningCourseIds = [] } =
        parentFiltersData?.LEARNING_COURSE ?? {};
      options.filters.learningCourseIds = learningCourseIds;

      const { values: courses = [] } = parentFiltersData?.COURSE ?? {};
      options.filters.courses = _.uniq(courses);

      const { values: academicYears = [] } =
        parentFiltersData?.ACADEMIC_YEAR ?? [];
      options.filters.academicYears = academicYears;

      return options;
    }

    case "EVIDENCE_FOLDER": {
      const { values: courses } = parentFiltersData?.COURSE ?? [];
      const options = {
        filters: {
          courseIds: courses,
        },
      };

      return options;
    }
    case "FOLDER": {
      const options = {
        filters: { curriculumProgramIds: [curriculumProgramId] },
      };

      const { values: courses } = parentFiltersData?.COURSE ?? [];
      options.courseIds = courses;

      return options;
    }

    case "PROGRESS_REPORT_TASK_GROUP": {
      const options = {
        filters: {
          curriculumProgramIds: [curriculumProgramId],
        },
      };

      const { values: academicYears = [] } =
        parentFiltersData.ACADEMIC_YEAR ?? {};
      options.filters.academicYearId = academicYears[0];

      return options;
    }

    case ELEMENT_TYPE_MYP_ATL:
    case ELEMENT_TYPE_MYP_KEY_CONCEPT:
    case ELEMENT_TYPE_MYP_LEARNER_PROFILE:
    case ELEMENT_TYPE_MYP_OBJECTIVES:
    case ELEMENT_TYPE_MYP_SERVICE_ACTION_OUTCOME:
    case ELEMENT_TYPE_DP_CP_SL_LEARNING_OUTCOMES:
    case ELEMENT_TYPE_DP_CAS_LEARNING_OUTCOMES:
    case ELEMENT_TYPE_UBD_ATL: {
      return {
        curriculumId: curriculumProgramId,
        filters: {
          elementTypes: [entityType],
        },
      };
    }

    case "STUDENT": {
      const { values: academicYears = [] } =
        parentFiltersData.ACADEMIC_YEAR ?? {};

      const { values: courses = [] } = parentFiltersData?.COURSE ?? {};

      /* The STUDENT entity schema currently does not include a learning course filter.  
         To work around this, we pass all classes from the selected learning courses  
         and use them to filter the student options accordingly. */

      const passAvailableClassOptions =
        _.isEmpty(parentFiltersData?.COURSE?.values) &&
        !_.isEmpty(parentFiltersData?.LEARNING_COURSE?.values);

      const updatedCourses = passAvailableClassOptions
        ? _.uniq(
            _.flatMap(activeTabClassesFilterOptions, ({ options }) =>
              _.map(options, "value")
            )
          )
        : courses;

      return {
        filters: {
          academicYearId: academicYears[0],
          archivalState: "ACTIVE",
          curriculumProgramIds: [curriculumProgramId],
          courses: updatedCourses,
        },
      };
    }

    case "SUBJECT_GROUP": {
      return {
        filters: {
          curriculumProgramIds: [curriculumProgramId],
        },
      };
    }
  }
};

export const getFilterParam = ({
  entityType,
  parentFiltersData,
  entitySubType,
}) => {
  switch (entityType) {
    case ELEMENT_TYPE_MYP_ATL:
    case ELEMENT_TYPE_UBD_ATL: {
      return { startDepth: 1, upToDepth: 1 };
    }
    case ELEMENT_TYPE_MYP_KEY_CONCEPT:
    case ELEMENT_TYPE_MYP_LEARNER_PROFILE: {
      return { startDepth: 0, upToDepth: 0 };
    }
    case ELEMENT_TYPE_DP_CAS_LEARNING_OUTCOMES:
    case ELEMENT_TYPE_DP_CP_SL_LEARNING_OUTCOMES:
    case ELEMENT_TYPE_MYP_SERVICE_ACTION_OUTCOME:
      return { startDepth: 0, upToDepth: 2 };
    case ELEMENT_TYPE_MYP_OBJECTIVES: {
      switch (entitySubType) {
        case "INTERDISCIPLINARY_CRITERIA":
          return {
            subTypes: ["INTERDISCIPLINARY_CRITERIA"],
            startDepth: 1,
            upToDepth: 2,
          };
        case "PERSONAL_OBJECTIVES":
          return {
            subTypes: ["PERSONAL_OBJECTIVES"],
            startDepth: 1,
            upToDepth: 2,
          };
        case "COMMUNITY_OBJECTIVES":
          return {
            subTypes: ["COMMUNITY_OBJECTIVES"],
            startDepth: 1,
            upToDepth: 2,
          };
        default: {
          const { subjects = [] } = parentFiltersData.COURSE ?? {};
          return {
            associatedParents: [{ ids: subjects, type: "SUBJECT" }],
            startDepth: 1,
            upToDepth: 2,
          };
        }
      }
    }
  }
};

const getLabel = ({
  key,
  values,
  defaultNameSortPreference = "FIRST_NAME",
}) => {
  switch (key) {
    case "ACADEMIC_YEAR": {
      const [startDate, endDate] = values;
      return getYearDurationFormat({ startDate, endDate });
    }
    case "STUDENT": {
      const [firstName, lastName] = values;
      return defaultNameSortPreference === "LAST_NAME"
        ? `${lastName}, ${firstName}`
        : `${firstName} ${lastName}`;
    }
    default: {
      return _.join(values, " ");
    }
  }
};

/**
 *
 * @param {*} param0
 * @param {Array} param0.attributes - Array of values. Each value specifies
 *                                          property of mainFacets/nestedFacets to extract label
 *
 */
export const getOptions = ({
  mainFacets,
  attributes,
  key,
  defaultNameSortPreference = "FIRST_NAME",
  t,
}) => {
  return _.map(mainFacets, option => {
    const id = _.get(option, "id", "");
    const labels = _.map(attributes, attribute => _.get(option, attribute, ""));
    const label = getLabel({ key, values: labels, defaultNameSortPreference });
    const isArchived = _.get(option, "isArchived", false);

    switch (key) {
      case "UNIT_PLAN_THEME": {
        return { label, value: option.uid, isArchived };
      }
      case "EVIDENCE_FOLDER": {
        const color = _.get(option, "color", "");
        return { label, value: id, isArchived, color };
      }
      case "STUDENT": {
        return {
          label: getFormattedUserName({
            user: option,
            type: "student",
          }),
          value: id,
          isArchived,
        };
      }
      case "LEARNING_COURSE": {
        const subText =
          _.get(option, "type", "") == "TEACHER_COURSE"
            ? t("academicSetup:teacher_course")
            : t("course:blueprint_course_lowercase");

        return {
          label,
          value: id,
          subText,
        };
      }

      default: {
        return { label, value: id, isArchived };
      }
    }
  });
};

export const getFilterListOptionsV2 = ({ nestedFacets, attributes, key }) => {
  return _.map(nestedFacets, nestedFacet => {
    const nestedFacetId = _.get(nestedFacet, "id", "");
    const nestedFacetLabel = _.get(nestedFacet, "label", "");
    const mainFacets = _.get(nestedFacet, "mainFacets", []);
    return {
      key: nestedFacetId,
      label: nestedFacetLabel,
      options: getOptions({ mainFacets, attributes, key }),
    };
  });
};

/**It is expected that all nodes belongs to one subject */
const getMypObjectives = ({ nodes }) => {
  const parentIdToChildNodesMapping = {};

  let options = [];

  /**This loop will do two things
   * a. separate root nodes and store it in options array
   * b. create mapping between parent and all its children
   */
  _.forEach(nodes, ({ id, parent, label }) => {
    if (parent) {
      if (parentIdToChildNodesMapping[parent]) {
        parentIdToChildNodesMapping[parent].push({ label, value: id });
      } else {
        parentIdToChildNodesMapping[parent] = [{ label, value: id }];
      }
    } else {
      options.push({ key: id, label });
    }
  });

  /**Here we are attaching children  with respective parent*/
  options = _.map(options, ({ key, label }) => {
    return {
      key,
      label,
      options: parentIdToChildNodesMapping[key],
      isLeafNode: true,
    };
  });

  return options;
};

export const getMypObjectivesGroupedBySubject = ({
  nodes,
  allSubjects = [],
}) => {
  const groupedNodes = _.groupBy(nodes, ({ associatedParents }) => {
    return _.get(_.first(associatedParents), "id", "");
  });

  const subjects = _.filter(_.keys(groupedNodes), key => key != "");

  const options = [];

  _.forEach(subjects, subject => {
    const objectives = getMypObjectives({ nodes: groupedNodes[subject] });

    const subjectDetail = _.find(allSubjects, { id: subject });

    /**
     * isLeafNode is set to true if
     * either objectives are part of interdisciplinary objectives
     * or they belongs year wise objectives
     */
    options.push({
      key: subject,
      label: _.get(subjectDetail, "subjectName", ""),
      options: objectives,
      isLeafNode: false,
    });
  });

  const interDisciplinaryNodes = _.filter(
    nodes,
    ({ associatedParents }) => !associatedParents
  );

  const interDisciplinaryObjectives = _.map(
    interDisciplinaryNodes,
    ({ id, label }) => ({ value: id, label })
  );

  if (!_.isEmpty(interDisciplinaryObjectives)) {
    options.push({
      key: "INTERDISCIPLINARY_CRITERIA",
      label: "Interdisciplinary objectives",
      options: interDisciplinaryObjectives,
      isLeafNode: true,
    });
  }

  return options;
};

export const getFilterOptions = ({
  levels,
  attributes,
  data,
  entityType,
  defaultNameSortPreference = "FIRST_NAME",
  allSubjects,
  t,
}) => {
  let mainFacets = [];
  if (levels === 1) {
    switch (entityType) {
      case ELEMENT_TYPE_MYP_ATL:
      case ELEMENT_TYPE_MYP_KEY_CONCEPT:
      case ELEMENT_TYPE_MYP_LEARNER_PROFILE:
      case ELEMENT_TYPE_MYP_OBJECTIVES:
      case ELEMENT_TYPE_MYP_SERVICE_ACTION_OUTCOME:
      case ELEMENT_TYPE_DP_CAS_LEARNING_OUTCOMES:
      case ELEMENT_TYPE_DP_CP_SL_LEARNING_OUTCOMES:
      case ELEMENT_TYPE_UBD_ATL: {
        mainFacets = _.get(data, "genericFacets.mainFacets[0].nodes", []);
        break;
      }
      case "SUBJECT_GROUP": {
        mainFacets = _.filter(
          _.get(data, "genericFacets.mainFacets", []),
          ({ subjects }) => {
            return !_.isEmpty(subjects);
          }
        );
        break;
      }
      default: {
        mainFacets = _.get(data, "genericFacets.mainFacets", []);
      }
    }

    return getOptions({
      mainFacets,
      attributes,
      key: entityType,
      defaultNameSortPreference,
      t,
    });
  } else if (entityType === ELEMENT_TYPE_MYP_OBJECTIVES) {
    const nodes = _.get(data, "genericFacets.mainFacets[0].nodes", []);

    return getMypObjectivesGroupedBySubject({ nodes, allSubjects });
  } else {
    const nestedFacets = _.get(data, "genericFacets.nestedFacets", []);

    return getFilterListOptionsV2({
      nestedFacets,
      attributes,
      key: entityType,
    });
  }
};

export const getFilterDisabilityStatusConfig = ({
  parentFiltersData,
  config,
  totalOptions,
  isLoading,
}) => {
  if (isLoading) {
    return { isDisabled: true, stateCode: "LOADING" };
  }
  const totalParentFilters = _.size(config);

  for (let i = 0; i < totalParentFilters; i++) {
    const { parent: parentFilter, selectOnlyOneOptionInParentFilter } = config[
      i
    ];
    const { values, label } = parentFiltersData[parentFilter];

    if (selectOnlyOneOptionInParentFilter && _.size(values) !== 1) {
      return {
        isDisabled: true,
        stateCode: "PARENT_FILTER_MULTI_VALUE",
        parentFilterLabel: label,
      };
    }
  }

  const isDisabled = totalOptions === 0;

  if (isDisabled) {
    return { isDisabled, stateCode: "NO_OPTIONS" };
  }

  return { isDisabled: false };
};

export const getDependentFilters = ({
  parent,
  activeTab,
  curriculumType,
  currentTabFiltersConfig,
}) => {
  const dependentFilterConfig = _.get(
    currentTabFiltersConfig,
    `${activeTab}.dependentFilterConfig`,
    []
  );

  const filters = _.get(
    currentTabFiltersConfig,
    `${activeTab}.curriculumWiseAvailableFilters.${curriculumType}`,
    []
  );

  /**Only consider those dependent Filters that are available for current curriculum */
  const filteredDependentFilterConfig = _.filter(
    dependentFilterConfig,
    ({ filter }) => _.includes(filters, filter)
  );

  const dependentFilters = [];

  _.forEach(filteredDependentFilterConfig, ({ filter, parents }) => {
    const isParentPresent =
      _.findIndex(
        parents,
        ({ parent: parentFilter, curriculumType: validCurriculum }) => {
          const isAllowedByCurriculum =
            !validCurriculum || curriculumType === validCurriculum;
          return parent === parentFilter && isAllowedByCurriculum;
        }
      ) > -1;

    if (isParentPresent) {
      dependentFilters.push(filter);
    }
  });

  return dependentFilters;
};

export const getValidMypObjectivesStatus = ({ optionList, valueList }) => {
  const updatedValues = [];

  let areValuesUpdated = false;

  _.forEach(valueList, ({ values, key, isLeafNode }) => {
    const { options } = _.find(
      optionList,
      ({ key: optionKey }) => key == optionKey
    );

    if (isLeafNode) {
      const filteredValues = _.filter(values, value =>
        _.findIndex(
          options,
          ({ value: optionValue }) => optionValue == value > -1
        )
      );

      if (_.size(values) != _.size(filteredValues)) {
        areValuesUpdated = true;
      }

      updatedValues.push({ key, values: filteredValues });
    } else {
      const {
        areValuesUpdated: areSubGroupValuesUpdated,
        updatedValues: updatedSubGroupValues,
      } = getValidMypObjectivesStatus({
        optionList: options,
        valueList: values,
      });

      areValuesUpdated = areValuesUpdated || areSubGroupValuesUpdated;

      if (!_.isEmpty(updatedSubGroupValues)) {
        updatedValues.push({ key, values: updatedSubGroupValues });
      }
    }
  });

  return { areValuesUpdated, updatedValues };
};

export const getValidFilterValuesStatus = ({
  options,
  values,
  entityType,
  projectNodeUID,
}) => {
  switch (entityType) {
    case "COURSE": {
      const valueList = values;
      const optionList = options;
      const updatedValues = [];

      let areValuesUpdated = false;
      _.forEach(valueList, ({ key, values }) => {
        const options = _.find(optionList, ["key", key])?.options ?? [];

        const filteredValues = _.filter(values, value => {
          return _.findIndex(options, ["value", value]) > -1;
        });

        if (_.size(values) !== _.size(filteredValues)) {
          areValuesUpdated = true;
        }

        updatedValues.push({ key, values: filteredValues });
      });

      return { areValuesUpdated, updatedValues };
    }

    case ELEMENT_TYPE_MYP_OBJECTIVES: {
      if (_.includes(["CP_DETAILS" || "PP_DETAILS"], projectNodeUID)) {
        let updatedValues = _.filter(options, ({ value }) => {
          return _.includes(values, value);
        });
        updatedValues = _.map(updatedValues, ({ value }) => value);
        const areValuesUpdated = _.size(updatedValues) !== _.size(values);

        return { areValuesUpdated, updatedValues };
      }
      return getValidMypObjectivesStatus({
        optionList: options,
        valueList: values,
      });
    }

    default: {
      let updatedValues = _.filter(options, ({ value }) => {
        return _.includes(values, value);
      });
      updatedValues = _.map(updatedValues, ({ value }) => value);
      const areValuesUpdated = _.size(updatedValues) !== _.size(values);

      return { areValuesUpdated, updatedValues };
    }
  }
};

/**If filter is part of set(ex. planner element set), then extract label from planner element set
 * else use filterLocale
 */
export const getFilterLabel = ({
  plannerElementSets,
  isLabelPresentInSetLabels,
  labelPresentInSetLabelsPerm,
  filterLocale,
  entityType,
  curriculumType,
  localeParams,
  t,
}) => {
  const isDynamicPlannerElementLabel =
    (labelPresentInSetLabelsPerm &&
      ACLStore.can(labelPresentInSetLabelsPerm)) ||
    isLabelPresentInSetLabels;

  switch (entityType) {
    case "SUBJECT": {
      /**
       * Use dynamic subject label only for UBD and MYP. For MYP, we will use either dynamic label or
       * static label based on perm
       */
      if (
        isDynamicPlannerElementLabel &&
        _.includes([CURRICULUM_TYPE_MYP, CURRICULUM_TYPE_UBD], curriculumType)
      ) {
        return plannerElementSets[
          CURRICULUM_TYPE_TO_LEARNING_STANDARD_MAPPING[curriculumType]
        ]?.label;
      }

      if (curriculumType == CURRICULUM_TYPE_MYP) {
        return t("common:subject_plural");
      }

      return t(filterLocale);
    }
    default: {
      if (isDynamicPlannerElementLabel) {
        return plannerElementSets[entityType]?.label;
      }
      return t(filterLocale, localeParams);
    }
  }
};

export const getPortfolioFolderFilterTooltip = ({
  t,
  filterDisabilityStatusConfig,
}) => {
  const { stateCode, parentFilterLabel } = filterDisabilityStatusConfig;

  switch (stateCode) {
    case "LOADING": {
      return t("snp:loading_options");
    }

    case "NO_OPTIONS": {
      return t("progressReport:no_folders_available_in_the_selected_class");
    }

    case "PARENT_FILTER_MULTI_VALUE": {
      return t("progressReport:portfolio_folder_single_select_tooltip_text");
    }

    default: {
      return null;
    }
  }
};

const getTabFilterConfig = ({ academicYearId, curriculumProgramId }) => {
  const isLearningCoursesEnabled = getIsLearningCoursesEnabled({
    academicYearId,
    curriculumProgramId,
  });

  const shouldEnableLearningCourses =
    ACLStore.can(LEARNING_COURSE_FILTER_IN_SNP) && isLearningCoursesEnabled;

  return shouldEnableLearningCourses
    ? tabFiltersConfigWithLearningCourse
    : tabFiltersConfig;
};

export const getTabFilterConfigMemoized = _.memoize(
  getTabFilterConfig,
  ({ academicYearId, curriculumProgramId }) => {
    return JSON.stringify({
      academicYearId,
      curriculumProgramId,
    });
  }
);
